import React, { useEffect, useState } from 'react';
import { makeRequest2 } from '../../../../utils/makeRequest';
import { toastify } from '../../../../utils/toast';

/**
 * Bulk-add analog meters from a CSV. Shared by the Water and Power analog tabs:
 * `importUrl` is the facility's import endpoint, `power` adds the meter type column.
 */
const AnalogMeterImportModal = ({ facilityId, importUrl, power = false, onHide, onImported }) => {
    const [units, setUnits] = useState([]);
    const [file, setFile] = useState(null);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        // The template lists every unit, so only the meter numbers have to be typed in.
        const loadUnits = async () => {
            try {
                const response = await makeRequest2(`/api/app/unit_management/get_facility_units/${facilityId}`, 'GET');
                if (response.success) {
                    const data = response.data?.units || response.data || [];
                    setUnits(Array.isArray(data) ? data : []);
                }
            } catch (error) {
                console.error('Could not load units for the template', error);
            }
        };
        if (facilityId) loadUnits();
    }, [facilityId]);

    const quote = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

    const downloadTemplate = () => {
        const header = power
            ? 'meter_number,unit_name,meter_type,initial_reading,customer_email,customer_phone'
            : 'meter_number,unit_name,initial_reading,customer_email,customer_phone';

        const lines = units.length > 0 ? units.map((unit) => unit.name || unit.unitNumber || '') : [''];
        const rows = lines.map((unitName) => (power ? `,${quote(unitName)},2 phase,0,,` : `,${quote(unitName)},0,,`));

        const url = URL.createObjectURL(new Blob([[header, ...rows].join('\n')], { type: 'text/csv' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = power ? 'analog_power_meters_template.csv' : 'analog_water_meters_template.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file) {
            toastify('Please select a CSV file', 'error');
            return;
        }

        try {
            setImporting(true);
            setResult(null);
            const formData = new FormData();
            formData.append('file', file);

            const response = await makeRequest2(`${importUrl}/${facilityId}`, 'POST', formData, { timeout: 5 * 60 * 1000 });

            if (response.success) {
                setResult(response.data);
                if (response.data?.created > 0 || response.data?.results?.created > 0) {
                    toastify(response.data.message, response.data.partial ? 'warning' : 'success');
                    if (onImported) onImported();
                }
            } else {
                toastify(response.error || 'Failed to import meters', 'error');
            }
        } catch (error) {
            toastify('Error importing meters', 'error');
        } finally {
            setImporting(false);
        }
    };

    const errors = result?.results?.errors || [];
    const warnings = result?.results?.warnings || [];

    return (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Import Analog {power ? 'Power' : 'Water'} Meters</h5>
                        <button type="button" className="btn-close" onClick={onHide} disabled={importing}></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            <p>
                                <button type="button" className="btn btn-link p-0" onClick={downloadTemplate}>
                                    Download template (.csv) with all {units.length || ''} units
                                </button>
                            </p>
                            <div className="alert alert-info">
                                <ul className="mb-0">
                                    <li>Fill in <strong>meter_number</strong> for each unit that has a meter; rows left blank are skipped</li>
                                    <li><strong>unit_name</strong> must match a unit in this facility</li>
                                    {power && <li><strong>meter_type</strong> is <em>2 phase</em> or <em>3 phase</em> (2 phase if left empty)</li>}
                                    <li><strong>initial_reading</strong> is the meter's current reading (0 if empty)</li>
                                    <li>
                                        <strong>customer_email</strong> or <strong>customer_phone</strong> is optional. A customer
                                        gets their postpaid {power ? 'power' : 'water'} account for this meter
                                    </li>
                                </ul>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Upload Completed CSV File</label>
                                <input
                                    type="file"
                                    accept=".csv"
                                    className="form-control"
                                    onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
                                    disabled={importing}
                                />
                            </div>

                            {result && (
                                <div className={`alert mb-0 ${errors.length === 0 && warnings.length === 0 ? 'alert-success' : 'alert-warning'}`}>
                                    <strong>{result.message}</strong>
                                    {(errors.length > 0 || warnings.length > 0) && (
                                        <ul className="small mb-0 mt-2" style={{ maxHeight: '12rem', overflowY: 'auto' }}>
                                            {errors.map((item) => (
                                                <li key={`e-${item.row}`}>Row {item.row} ({item.meter_number}): {item.error}</li>
                                            ))}
                                            {warnings.map((item) => (
                                                <li key={`w-${item.row}`}>Row {item.row} ({item.meter_number}): {item.warning}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-outline-secondary" onClick={onHide} disabled={importing}>
                                {result ? 'Close' : 'Cancel'}
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={importing || !file}>
                                {importing ? <span className="spinner-border spinner-border-sm me-1"></span> : null}
                                Import
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AnalogMeterImportModal;
